import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { colors } from './styles';

const COMP_BASE_URL = 'https://comp.erg.zone';

const COMPETITIONS_PAGE_SIZE = 20;

const SPORT_FEATURES: Record<string, { label: string; color: string }> = {
  // Match mobile `ergTypeColor` (mobile/shared/utils.ts)
  row: { label: 'Row', color: '#00C9BE' },
  ski: { label: 'Ski', color: '#2196F3' },
  bike: { label: 'Bike', color: '#FF9800' },
  run: { label: 'Run', color: '#8BC34A' },
  'fan-bike': {
    label: 'Fan Bike',
    color: '#f8bc1e',
  },
  multi: {
    label: 'Multi',
    color: '#673AB7',
  },
};

export interface CompetitionTrack {
  id: string;
  name: string;
  description?: string;
  compDate?: string;
  compDateDisplay?: string;
  compStatus?: string | null;
  webPath?: string | null;
  idToken?: string;
  features?: string[];
  imageUrl?: string;
  featuredImage?: string;
  source?: { id: string; name: string; slug: string; imageUrl?: string };
  workoutsCount?: number;
  usersCount?: number;
}

function getWorkoutsCount(comp: CompetitionTrack): number {
  return comp.workoutsCount ?? 0;
}

function getRegisteredAthletesCount(comp: CompetitionTrack): number {
  return comp.usersCount ?? 0;
}

function getSportTags(
  features: string[] | undefined
): { key: string; label: string; color: string }[] {
  if (!features || features.length === 0) return [];
  return features
    .filter((f) => SPORT_FEATURES[f])
    .map((f) => ({ key: f, ...SPORT_FEATURES[f] }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function getCompetitionTypeLabel(comp: CompetitionTrack): string {
  const features = comp.features || [];
  if (features.includes('race')) return 'Live Race';
  return 'Competition';
}

function getCompOrganizerName(comp: CompetitionTrack): string {
  return (comp.source?.name ?? '').trim();
}

function getCompYear(comp: CompetitionTrack): number | null {
  const raw = comp.compDate || comp.compDateDisplay;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear();
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    filtersSectionHeader: {
      marginBottom: theme.spacing(2),
    },
    filtersTitle: {
      fontWeight: 700,
      fontSize: '1.25rem',
      lineHeight: 1.3,
      marginBottom: theme.spacing(0.75),
    },
    filtersSubheader: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      lineHeight: 1.45,
      maxWidth: 720,
      [theme.breakpoints.down('sm')]: {
        fontSize: '0.8125rem',
      },
    },
    filtersCard: {
      width: '100%',
      borderRadius: theme.shape.borderRadius * 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2.5),
      marginBottom: theme.spacing(3),
      display: 'grid',
      gridTemplateColumns: '1.5fr 1.25fr 1fr',
      columnGap: theme.spacing(2.5),
      rowGap: theme.spacing(2),
      alignItems: 'start',
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '1fr 1fr',
        padding: theme.spacing(2),
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        alignItems: 'stretch',
        padding: theme.spacing(1.5),
      },
    },
    filterControl: {
      width: '100%',
    },
    sportControl: {
      gridColumn: '1 / -1',
      marginTop: theme.spacing(2),
    },
    sportSectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      marginBottom: theme.spacing(0.25),
    },
    sportSectionTitle: {
      display: 'block',
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },
    sportFieldLabel: {
      display: 'block',
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.25),
    },
    sportChips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1.5),
      padding: theme.spacing(0.5, 0),
      '& > *': {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
      },
    },
    sportChip: {
      height: 24,
      borderRadius: 16,
      fontWeight: 600,
      '& .MuiChip-label': {
        paddingLeft: 16,
        paddingRight: 16,
        fontSize: 12,
        lineHeight: '24px',
      },
    },
    filterMetaRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    filterCount: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
    card: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform .2s ease-in-out, box-shadow .2s ease-in-out',
      borderRadius: theme.shape.borderRadius * 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
      },
    },
    cardMedia: {
      aspectRatio: '2.5',
      position: 'relative',
      backgroundColor: theme.palette.grey[300],
    },
    sourceImageOverlay: {
      position: 'absolute',
      top: 12,
      right: 12,
      width: 40,
      height: 40,
      borderRadius: '50%',
      border: '2px solid rgba(255, 255, 255, 0.9)',
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      zIndex: 1,
      overflow: 'hidden',
    },
    sourceImageCircle: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    cardContent: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(2),
    },
    trackName: {
      fontWeight: 600,
      lineHeight: 1.3,
      marginBottom: theme.spacing(1),
    },
    sourceName: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      marginBottom: theme.spacing(1),
    },
    link: {
      textDecoration: 'none',
      color: 'inherit',
      display: 'block',
      height: '100%',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      fontSize: 12,
      height: 24,
      borderRadius: 12,
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.body1?.fontWeight ?? 400,
      letterSpacing: theme.typography.body1?.letterSpacing ?? 'normal',
    },
    sportBadgeButton: {
      cursor: 'pointer',
      WebkitAppearance: 'none' as never,
      WebkitTapHighlightColor: 'transparent',
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },
    },
    emptyState: {
      textAlign: 'center',
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
    },
    pastList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    pastListItem: {
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2, 0),
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: theme.spacing(1.5, 0),
      },
    },
    pastLogoLink: {
      flexShrink: 0,
      textDecoration: 'none',
      marginRight: theme.spacing(2),
      lineHeight: 0,
      display: 'block',
      width: 260,
      maxWidth: '100%',
      [theme.breakpoints.down('sm')]: {
        marginRight: 0,
        width: '100%',
        maxWidth: 260,
        alignSelf: 'center',
      },
    },
    pastLogoCircle: {
      width: '100%',
      maxWidth: 260,
      height: 'auto',
      aspectRatio: '3 / 1',
      borderRadius: theme.shape.borderRadius,
      objectFit: 'contain',
      backgroundColor: theme.palette.grey[300],
      display: 'block',
      transition: 'transform .2s ease-in-out, box-shadow .2s ease-in-out',
    },
    pastLogoLinkHover: {
      '&:hover $pastLogoCircle': {
        transform: 'scale(1.05)',
        boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
      },
    },
    pastContent: {
      flex: 1,
      minWidth: 0,
    },
    pastTitleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
      flexWrap: 'wrap',
    },
    pastName: {
      fontWeight: 400,
      fontSize: '1.25rem',
      color: theme.palette.text.primary,
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.0625rem',
      },
    },
    pastNameLink: {
      color: 'inherit',
      textDecoration: 'none',
      display: 'inline-block',
      borderRadius: 4,
      padding: '2px 4px',
      margin: '-2px -4px',
      transition: 'background-color 150ms ease-in-out',
      '&:hover': {
        color: 'inherit',
        textDecoration: 'none',
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
    pastOrganizer: {
      fontSize: '0.875rem',
      color: colors.darkBackground,
      marginBottom: theme.spacing(0.5),
    },
    pastHostFilterLink: {
      color: theme.palette.text.primary,
      textDecoration: 'none',
      display: 'inline-block',
      borderRadius: 4,
      padding: '2px 4px',
      margin: '-2px -4px',
      transition: 'background-color 150ms ease-in-out',
      cursor: 'pointer',
      '&:hover': {
        color: theme.palette.text.primary,
        textDecoration: 'none',
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },
    },
    pastOrganizerRow: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    pastOrganizerLogo: {
      width: 20,
      height: 20,
      borderRadius: '50%',
      objectFit: 'cover',
      backgroundColor: theme.palette.grey[300],
      flexShrink: 0,
      marginLeft: theme.spacing(1),
    },
    hostOptionRow: {
      display: 'inline-flex',
      alignItems: 'center',
    },
    hostOptionLogo: {
      width: 18,
      height: 18,
      borderRadius: '50%',
      objectFit: 'cover',
      backgroundColor: theme.palette.grey[300],
      flexShrink: 0,
      marginRight: theme.spacing(2),
    },
    pastOrganizerLabel: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(0.5),
    },
    pastDate: {
      fontSize: '0.8125rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
    pastMeta: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      flexShrink: 0,
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
    },
    pastAction: {
      flexShrink: 0,
      alignSelf: 'flex-end',
      [theme.breakpoints.down('sm')]: {
        alignSelf: 'stretch',
        width: '100%',
      },
    },
    pastActionStack: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '10px',
      [theme.breakpoints.down('sm')]: {
        alignItems: 'stretch',
        width: '100%',
      },
    },
    pastJoinButton: {
      backgroundColor: colors.primaryColor,
      color: theme.palette.common.white,
      boxShadow: 'none',
      padding: theme.spacing(1, 2),
      borderRadius: theme.shape.borderRadius * 2,
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem',
      '&:hover': {
        backgroundColor: colors.secondaryColor,
        color: theme.palette.common.white,
        boxShadow: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        justifyContent: 'center',
      },
    },
    pastActionButton: {
      backgroundColor: colors.leaderboardColor,
      color: theme.palette.common.white,
      boxShadow: 'none',
      padding: theme.spacing(1, 2),
      borderRadius: theme.shape.borderRadius * 2,
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem',
      '&:hover': {
        backgroundColor: '#a4005a',
        color: theme.palette.common.white,
        boxShadow: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        justifyContent: 'center',
      },
    },
    loadMoreWrap: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(2),
    },
    loadMoreButton: {
      textTransform: 'none',
    },
  })
);

interface CompetitionListProps {
  competitions: CompetitionTrack[];
}

export default function CompetitionList({
  competitions,
}: CompetitionListProps) {
  const styles = useStyles();
  const [search, setSearch] = useState('');
  const [selectedSport, setSelectedSport] = useState<string | null>(null);
  const [organizer, setOrganizer] = useState<string>('any');
  const [year, setYear] = useState<string>('any');
  const [competitionsVisibleCount, setPastVisibleCount] = useState(
    COMPETITIONS_PAGE_SIZE
  );

  useEffect(() => {
    setPastVisibleCount(COMPETITIONS_PAGE_SIZE);
  }, [search, selectedSport, organizer, year]);

  const clearFilters = () => {
    setSearch('');
    setSelectedSport(null);
    setOrganizer('any');
    setYear('any');
  };

  const sportOptions = useMemo(() => {
    const used = new Set<string>();
    for (const c of competitions) {
      for (const f of c.features || []) {
        if (SPORT_FEATURES[f]) used.add(f);
      }
    }
    return Array.from(used).sort((a, b) =>
      SPORT_FEATURES[a].label.localeCompare(SPORT_FEATURES[b].label)
    );
  }, [competitions]);

  const organizerOptions = useMemo(() => {
    const byName = new Map<string, { name: string; imageUrl?: string }>();
    for (const c of competitions) {
      const name = getCompOrganizerName(c);
      if (!name) continue;
      const imageUrl = c.source?.imageUrl || undefined;
      const prev = byName.get(name);
      if (!prev) {
        byName.set(name, { name, imageUrl });
      } else if (!prev.imageUrl && imageUrl) {
        byName.set(name, { name, imageUrl });
      }
    }
    return Array.from(byName.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [competitions]);

  const yearOptions = useMemo(() => {
    const used = new Set<number>();
    for (const c of competitions) {
      const y = getCompYear(c);
      if (y) used.add(y);
    }
    return Array.from(used).sort((a, b) => b - a);
  }, [competitions]);

  const filteredCompetitions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return competitions.filter((c) => {
      if (q) {
        const haystack = `${c.name || ''} ${getCompOrganizerName(
          c
        )}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (selectedSport !== null) {
        const features = c.features || [];
        if (!features.includes(selectedSport)) return false;
      }
      if (organizer !== 'any') {
        if (getCompOrganizerName(c) !== organizer) return false;
      }
      if (year !== 'any') {
        const y = getCompYear(c);
        if (!y || String(y) !== year) return false;
      }
      return true;
    });
  }, [competitions, search, selectedSport, organizer, year]);

  const displayedCompetitions = useMemo(
    () => filteredCompetitions.slice(0, competitionsVisibleCount),
    [filteredCompetitions, competitionsVisibleCount]
  );

  const pastHasMore = competitionsVisibleCount < filteredCompetitions.length;

  const getLeaderboardUrl = (comp: CompetitionTrack) => {
    const token = comp.idToken || comp.id;
    return `${COMP_BASE_URL}/leaderboard/${token}`;
  };

  const renderPastListItem = (comp: CompetitionTrack) => {
    const source = comp.source || { name: '', imageUrl: '', slug: '', id: '' };
    const organizerName = getCompOrganizerName(comp);
    const logoUrl = comp.featuredImage || comp.imageUrl || source.imageUrl;
    const sportTags = getSportTags(comp.features);
    const workoutCount = getWorkoutsCount(comp);
    const registeredAthletesCount = getRegisteredAthletesCount(comp);
    const url = getLeaderboardUrl(comp);
    const joinPath = comp.webPath && String(comp.webPath).replace(/^\//, '');
    const joinHref = joinPath ? `/${joinPath}` : undefined;
    const showJoin =
      !!joinHref && ['open', 'registration'].includes(comp.compStatus || '');

    return (
      <li key={comp.id} className={styles.pastListItem}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${styles.pastLogoLink} ${styles.pastLogoLinkHover}`}
        >
          {logoUrl ? (
            <img src={logoUrl} alt="" className={styles.pastLogoCircle} />
          ) : (
            <div className={styles.pastLogoCircle} />
          )}
        </a>
        <div className={styles.pastContent}>
          <div className={styles.pastTitleRow}>
            <Typography className={styles.pastName}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.pastNameLink}
              >
                {comp.name}
              </a>
            </Typography>
          </div>
          {organizerName && (
            <Typography className={styles.pastOrganizer}>
              <span className={styles.pastOrganizerRow}>
                <span className={styles.pastOrganizerLabel}>Organized by</span>
                <a
                  href="#competition-filters-panel"
                  className={styles.pastHostFilterLink}
                  aria-label={`Filter list by organizer ${organizerName}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setOrganizer(organizerName);
                    const panel = document.getElementById(
                      'competition-filters-panel'
                    );
                    if (panel && typeof panel.scrollIntoView === 'function') {
                      panel.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                    }
                  }}
                >
                  {organizerName}
                </a>
              </span>
            </Typography>
          )}
          <div className={styles.pastMeta}>
            <span
              className={styles.badge}
              style={
                getCompetitionTypeLabel(comp) === 'Live Race'
                  ? {
                      backgroundColor: colors.secondaryColor,
                      color: '#fff',
                      border: 'none',
                      fontWeight: 600,
                      paddingLeft: 16,
                      paddingRight: 16,
                      borderRadius: 16,
                    }
                  : {
                      backgroundColor: colors.leaderboardColor,
                      color: '#fff',
                      border: 'none',
                      fontWeight: 600,
                      paddingLeft: 16,
                      paddingRight: 16,
                      borderRadius: 16,
                    }
              }
            >
              {getCompetitionTypeLabel(comp)}
            </span>
            {workoutCount > 0 && (
              <span
                className={styles.badge}
                style={{
                  backgroundColor: 'transparent',
                  border: `2px solid ${colors.textColor}`,
                  color: colors.textColor,
                }}
              >
                {workoutCount} workout{workoutCount !== 1 ? 's' : ''}
              </span>
            )}
            {registeredAthletesCount > 0 && (
              <span
                className={styles.badge}
                style={{
                  backgroundColor: 'transparent',
                  border: `2px solid ${colors.textColor}`,
                  color: colors.textColor,
                }}
              >
                <i
                  className="fas fa-users"
                  aria-hidden="true"
                  style={{ marginRight: 6, fontSize: 12 }}
                />
                {registeredAthletesCount} athlete
                {registeredAthletesCount !== 1 ? 's' : ''}
              </span>
            )}
            {sportTags.map((sport) => (
              <button
                key={sport.key}
                type="button"
                className={`${styles.badge} ${styles.sportBadgeButton}`}
                style={{
                  backgroundColor: sport.color,
                  border: 'none',
                  color: '#fff',
                  fontWeight: 600,
                }}
                aria-label={`Filter list by ${sport.label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedSport(sport.key);
                  const panel = document.getElementById(
                    'competition-filters-panel'
                  );
                  if (panel && typeof panel.scrollIntoView === 'function') {
                    panel.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }
                }}
              >
                {sport.label}
              </button>
            ))}
          </div>
          {comp.compDateDisplay && (
            <Typography className={styles.pastDate}>
              <i
                className="far fa-calendar-check"
                aria-hidden="true"
                style={{ marginRight: 6, fontSize: 12 }}
              />
              Start Date: {comp.compDateDisplay}
            </Typography>
          )}
        </div>
        <div className={styles.pastAction}>
          <div className={styles.pastActionStack}>
            {showJoin && (
              <Button
                variant="contained"
                component="a"
                href={joinHref}
                className={styles.pastJoinButton}
              >
                <i
                  className="fas fa-user-plus"
                  aria-hidden="true"
                  style={{ marginRight: 8, fontSize: 14 }}
                />
                Register
              </Button>
            )}
            <Button
              variant="contained"
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.pastActionButton}
            >
              <i
                className="fas fa-trophy"
                aria-hidden="true"
                style={{ marginRight: 8, fontSize: 14 }}
              />
              View Leaderboard
            </Button>
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className={styles.root}>
      {competitions.length === 0 ? (
        <Typography className={styles.emptyState}>
          No past competitions to display.
        </Typography>
      ) : (
        <>
          <div id="competition-filters-panel">
            <div className={styles.filtersSectionHeader}>
              <Typography component="h2" className={styles.filtersTitle}>
                Filters
              </Typography>
              <Typography className={styles.filtersSubheader} component="p">
                Search and filter past competitions by name, organizer, date, or
                sport.
              </Typography>
            </div>
            <div className={styles.filtersCard}>
              <div className={styles.filterControl}>
                <span className={styles.sportFieldLabel}>Search</span>
                <TextField
                  placeholder="Competition or organizer…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  variant="outlined"
                  size="small"
                  className={styles.filterControl}
                />
              </div>

              <div className={styles.filterControl}>
                <span className={styles.sportFieldLabel}>Organizer</span>
                <FormControl
                  variant="outlined"
                  size="small"
                  className={styles.filterControl}
                >
                  <Select
                    value={organizer}
                    onChange={(e) => setOrganizer(String(e.target.value))}
                  >
                    <MenuItem value="any">All</MenuItem>
                    {organizerOptions.map((h) => (
                      <MenuItem key={h.name} value={h.name}>
                        <span className={styles.hostOptionRow}>
                          {h.imageUrl && (
                            <img
                              src={h.imageUrl}
                              alt={h.name}
                              className={styles.hostOptionLogo}
                            />
                          )}
                          <span>{h.name}</span>
                        </span>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className={styles.filterControl}>
                <span className={styles.sportFieldLabel}>Year</span>
                <FormControl
                  variant="outlined"
                  size="small"
                  className={styles.filterControl}
                >
                  <Select
                    value={year}
                    onChange={(e) => setYear(String(e.target.value))}
                  >
                    <MenuItem value="any">All</MenuItem>
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={String(y)}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className={`${styles.filterControl} ${styles.sportControl}`}>
                <div className={styles.sportSectionHeader}>
                  <span className={styles.sportSectionTitle}>Sport</span>
                  <Button
                    size="small"
                    onClick={clearFilters}
                    style={{ flexShrink: 0, textTransform: 'none' }}
                  >
                    Clear filters
                  </Button>
                </div>
                <div
                  className={styles.sportChips}
                  role="group"
                  aria-label="Sport filter"
                >
                  <Chip
                    label="Any"
                    clickable
                    className={styles.sportChip}
                    style={{
                      backgroundColor:
                        selectedSport === null
                          ? colors.leaderboardColor
                          : '#e0e0e0',
                      color: selectedSport === null ? '#fff' : '#616161',
                    }}
                    onClick={() => setSelectedSport(null)}
                  />

                  {sportOptions.map((key) => {
                    const selected = selectedSport === key;
                    const { label, color } = SPORT_FEATURES[key];
                    return (
                      <Chip
                        key={key}
                        clickable
                        className={styles.sportChip}
                        label={label}
                        style={{
                          backgroundColor: selected ? color : '#e0e0e0',
                          color: selected ? '#fff' : '#616161',
                        }}
                        onClick={() => {
                          setSelectedSport((prev) =>
                            prev === key ? null : key
                          );
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.filterMetaRow}>
            <Typography className={styles.filterCount}>
              Showing {displayedCompetitions.length} of{' '}
              {filteredCompetitions.length} matches
            </Typography>
          </div>

          {filteredCompetitions.length === 0 ? (
            <Typography className={styles.emptyState}>
              No competitions match your filters.
            </Typography>
          ) : (
            <>
              <ul className={styles.pastList}>
                {displayedCompetitions.map(renderPastListItem)}
              </ul>
              {pastHasMore && (
                <div className={styles.loadMoreWrap}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    className={styles.loadMoreButton}
                    onClick={() =>
                      setPastVisibleCount((n) => n + COMPETITIONS_PAGE_SIZE)
                    }
                  >
                    Load more (
                    {filteredCompetitions.length - competitionsVisibleCount}{' '}
                    left)
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {competitions.length === 0 && (
        <Typography className={styles.emptyState}>
          There are no competitions to display at the moment. Check back later
          or contact us to add your event.
        </Typography>
      )}
    </div>
  );
}
